// ===========================================================================
// Clinical Operations Dashboard (Module L) — capa de datos.
//
// Consolida los overview.json reales por location (DataDashCareManager) con
// open care gaps de centers/performance.json y métricas del día fake.
// Migración futura: GET /api/analytics/clinical-operations (ver fakeData.js).
// ===========================================================================

import {
  getDailyOpsProfile,
  getFallbackCareGaps,
  FALLBACK_LOCATIONS,
} from "./fakeData";

/** Normaliza nombres para el match centro ↔ performance ("Port St. Lucie" → "portstlucie"). */
const norm = (s = "") => s.toLowerCase().replace(/[^a-z0-9]/g, "");

/** Lista de locations (key + label) desde locations.json, con fallback. */
async function loadLocations() {
  try {
    const mod = await import(
      "../../../pages/Main/Temp/DataDashCareManager/locations.json"
    );
    const data = mod.default || mod;
    const locations = Array.isArray(data.locations) ? data.locations : [];
    if (!locations.length) return FALLBACK_LOCATIONS;
    return locations.map((l) => ({ key: l.key, label: l.label }));
  } catch (e) {
    return FALLBACK_LOCATIONS;
  }
}

/** Overview real de una location (kpis, census, health status). */
async function loadOverview(locationKey) {
  const mod = await import(
    `../../../pages/Main/Temp/DataDashCareManager/${locationKey}/overview.json`
  );
  return mod.default || mod;
}

/** Open care gaps reales por centro desde DataDashCEO_v2. */
async function loadCareGapsByCenter() {
  try {
    const mod = await import(
      "../../../pages/Main/Temp/DataDashCEO_v2/centers/performance.json"
    );
    const data = mod.default || mod;
    const map = new Map();
    (data.centers || []).forEach((c) => map.set(norm(c.centerName), c.openCareGaps || 0));
    return map;
  } catch (e) {
    return new Map();
  }
}

/**
 * Carga y agrega las métricas operativas de todos los centros.
 *
 * @returns {Promise<Object>} {
 *   signals: { visitsToday, noShowRate, postDischargePending, unmanagedHighRisk, openCareGaps },
 *   centers: [{ key, label, totalPatients, currentInpatient, highRiskCensus,
 *               unmanagedHighRisk, postDischargePending, erFrequentFlyers,
 *               inActiveCM, visitsToday, noShowRate, openCareGaps,
 *               healthStatusDistribution }]
 * }
 */
export async function getClinicalOperations() {
  const [locations, gapsByCenter] = await Promise.all([
    loadLocations(),
    loadCareGapsByCenter(),
  ]);

  const results = await Promise.allSettled(
    locations.map(async (loc) => {
      const overview = await loadOverview(loc.key);
      const daily = getDailyOpsProfile(loc.key, overview.totalPatients || 0);
      const openCareGaps =
        gapsByCenter.get(norm(loc.label)) ??
        getFallbackCareGaps(loc.key, overview.totalPatients || 0);
      return {
        key: loc.key,
        label: loc.label,
        totalPatients: overview.totalPatients || 0,
        currentInpatient: overview.currentInpatient || 0,
        highRiskCensus: overview.highRiskCensus || 0,
        unmanagedHighRisk: overview.kpis?.unmanagedHighRisk || 0,
        postDischargePending: overview.kpis?.postDischargePending || 0,
        erFrequentFlyers: overview.kpis?.erFrequentFlyers || 0,
        inActiveCM: overview.kpis?.inActiveCM || 0,
        visitsToday: daily.visitsToday,
        noShowRate: daily.noShowRate,
        openCareGaps,
        healthStatusDistribution: overview.healthStatusDistribution || {},
      };
    })
  );

  const centers = results
    .filter((r) => r.status === "fulfilled")
    .map((r) => r.value)
    .sort((a, b) => b.totalPatients - a.totalPatients);

  const sum = (fn) => centers.reduce((acc, c) => acc + fn(c), 0);
  const totalPatients = sum((c) => c.totalPatients) || 1;
  const noShowRate =
    sum((c) => c.noShowRate * c.totalPatients) / totalPatients; // promedio ponderado

  return {
    signals: {
      visitsToday: sum((c) => c.visitsToday),
      noShowRate,
      postDischargePending: sum((c) => c.postDischargePending),
      unmanagedHighRisk: sum((c) => c.unmanagedHighRisk),
      openCareGaps: sum((c) => c.openCareGaps),
    },
    centers,
  };
}
